import { useState, useEffect, useRef } from 'react';
import { useGlobalContext } from "../Context/GlobalContext";
import { useDeviceContext } from "../Context/DeviceContext";
import Flash from "./flash";
import Button from "../Components/button";
import Slider from "../Components/slider";
import { Grid2 as Grid } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CobberPattern, DeviceType, FlashCommand, FlashMode } from "../Types";
import Toggle from '../Components/toggle';
import Checkbox from '../Components/checkbox';


const FlashModeSettings = (
    { mode, handleModeUpdate, type, handleRemove }:
    { mode: FlashMode, handleModeUpdate: Function, type: DeviceType, handleRemove: Function }
) => {
    const { showPopup, hidePopup } = useGlobalContext();
    const { cartesianPatterns } = useDeviceContext();

    let {
        settings = {},
        config = {}
    } = mode;

    settings = {
        ...settings,
        brightness: {
            max: mode.settings?.brightness?.max ?? 1.5,
            min: mode.settings?.brightness?.min ?? 0.5
        }
    }

    const defaultSettings = {
        brightness: settings.brightness?.default ?? 1,
        speed: settings.speed?.default ?? 1,
        eyeSaver: false,
        brakeMode: 0,
        brakeSensitivity: 1,
        tiltSensitivity: 1
    };

    const [brightness, setBrightness] = useState<number>(
        config.brightness ? config.brightness : defaultSettings.brightness
    );
    const [speed, setFlashSpeed] = useState<number>(
        config.speed ?? defaultSettings.speed
    );
    const [eyeSaver, setEyeSaver] = useState<boolean>(
        config.eyeSaver ?? defaultSettings.eyeSaver
    );
    const [brakeMode, setBrakeMode] = useState<number>(
        config.brakeMode ?? mode.default_pattern ?? defaultSettings.brakeMode
    );
    const [brakeSensitivity, setBrakeSensitivity] = useState<number>(
        config.brakeSensitivity ?? defaultSettings.brakeSensitivity
    );
    const [tiltSensitivity, setTiltSensitivity] = useState<number>(
        config.tiltSensitivity ?? defaultSettings.tiltSensitivity
    );
    const [pendingSave, setPendingSave] = useState<Boolean>(false);
    
    const didMount = useRef(false);
    useEffect(() => {
        if (!didMount.current) {
            didMount.current = true;
            return;
        }

        setPendingSave(true);
    }, [brightness, speed, eyeSaver, brakeMode, brakeSensitivity, tiltSensitivity])

    const handleApply = async () => {
        hidePopup();
        handleModeUpdate({ 
            ...mode,
            config: { 
                brightness: brightness, 
                speed, 
                eyeSaver,
                brakeMode,
                brakeSensitivity,
                tiltSensitivity
            }
        });
        setPendingSave(false);
    }

    const handleReset = () => {
        setBrightness(defaultSettings.brightness);
        setFlashSpeed(defaultSettings.speed);
        setBrakeMode(defaultSettings.brakeMode);
        setBrakeSensitivity(defaultSettings.brakeSensitivity)
        setBrakeSensitivity(defaultSettings.tiltSensitivity)
    }

    const handleCancel = () => {
        hidePopup();
    }

    const maxBrightness = (settings?.brightness?.max ?? 1.5);
    const minBrightness = (settings?.brightness?.min ?? 0.5);
    const maxSpeed = (settings?.speed?.max ?? 1.5);
    const minSpeed = (settings?.speed?.min ?? 0.5);
    const maxSensitivity = (settings?.x_sensitivity?.max ?? 0.5);
    const minSensitivity = (settings?.x_sensitivity?.min ?? 1.5);
    const maxBrakeSensitivity = (settings?.z_sensitivity?.max ?? 0.5);
    const minBrakeSensitivity = (settings?.z_sensitivity?.min ?? 1.5);

    let hasFlashSpeed = false;
    if(cartesianPatterns) {
        const pattern = mode.flash_pattern as unknown as CobberPattern;
        const numberOfTimeBytes = pattern.x_patterns!
            .flat()
            .filter((c: FlashCommand) => c.command === 'timeByte')
            .length;
        hasFlashSpeed = hasFlashSpeed || numberOfTimeBytes > 1;
    } else {
        hasFlashSpeed = mode.mode_type === 'flash';
    }

    return (
        <div className="flash-mode-settings">
            <div className="flash-wrapper" style={cartesianPatterns ? {} : { width: '60%' }}>
                <Flash
                    pattern={eyeSaver ? mode.eye_saver_pattern : mode.flash_pattern}
                    multipliers={{ brightness, speed }}
                    type={type.id}
                    controlled={true}
                    tilt={mode.tilt}
                    brakeMode={brakeMode}
                    hasBrake={!!mode.brake_patterns_data?.length}
                />
            </div>
            <Grid container  spacing={1} sx={{ marginTop: '20px' }}>
                {(mode.allow_custom || (cartesianPatterns && mode.tilt)) && <Grid size={{ xs: 12 }}>
                    <div className="mode-settings">
                        {mode.allow_custom && <Slider
                            label={'ADJUST BRIGHTNESS'}
                            value={brightness}
                            setValue={(value: number) => setBrightness(value)}
                            max={maxBrightness}
                            min={minBrightness}
                            step={(maxBrightness - minBrightness) / 4}
                            leftLabel={'LOW'}
                            rightLabel={`HIGH`}
                            bottomLeftLabel={'SHORTER'}
                            bottomRightLabel={`LONGER`}
                            bottomLabel={`RUN TIME`}
                        />}
                        {!(mode.tilt && cartesianPatterns) && hasFlashSpeed && <Slider
                            label={settings?.speed?.name ?? 'ADJUST FLASH SPEED'}
                            value={speed}
                            setValue={(value: number) => setFlashSpeed(value)}
                            max={maxSpeed}
                            min={minSpeed}
                            leftLabel={settings?.speed?.minlabel ?? 'LOW'}
                            rightLabel={settings?.speed?.maxlabel ?? 'HIGH'}
                            step={((maxSpeed - minSpeed) / (settings?.speed?.steps ? (settings.speed.steps - 1) : 4))}
                        />}
                        {cartesianPatterns && mode.tilt && <Slider
                            label={'ADJUST TILT SENSITIVITY'}
                            value={tiltSensitivity}
                            setValue={(value: number) => setTiltSensitivity(value)}
                            max={maxSensitivity}
                            min={minSensitivity}
                            leftLabel={'LOW'}
                            rightLabel={'HIGH'}
                            step={((maxSensitivity - minSensitivity) / (settings?.x_sensitivity?.steps ? (settings.x_sensitivity.steps - 1) : 4))}
                        />}
                    </div>
                </Grid>}
                <Grid size={{ xs: 6 }}>
                    {!!mode.brake_patterns_data?.length && <div
                        className="brake-settings"
                        style={{ marginTop: '10px', padding: '10px 15px 20px 15px', position: 'relative' }}
                    >
                        <InfoOutlinedIcon
                            sx={{ cursor: 'pointer', marginLeft: '10px', top: 0, right: 0 }}
                            className='top-right'
                            onClick={() => showPopup({
                                content:
                                    <div className="accelerometer-info">
                                        <InfoOutlinedIcon
                                            className="top-left"
                                            sx={{ cursor: "pointer" }}
                                        />
                                        <p>WHEN BRAKING IS DETECTED,THE LIGHT MODE INTENSITY WILL SPIKE.</p>
                                        {mode.brake_patterns_data?.map((p: any) => {
                                            return <p>{p.name.toUpperCase()}: {p.description}</p>
                                        })}
                                    </div>,
                                titleIcon: <InfoOutlinedIcon />,
                                customClass: "button-small"
                            })}
                        />
                        <div 
                            className="header-underline" 
                            style={{textWrap: 'nowrap', marginTop: '15px'}}
                        >
                            BRAKE REFLEX MODE:
                        </div>
                         <div style={{ marginTop: '15px' }}>
                            <Checkbox
                                label='OFF'
                                value={brakeMode === 0}
                                setValue={() => setBrakeMode(0)}
                                inverse={true}
                                flipped={true}
                            />
                        </div>
                        {
                            mode.brake_patterns_data?.map((p: any) => {
                                return <div key={p.id} style={{ marginTop: '5px' }}>
                                    <Checkbox
                                        label={p.name.toUpperCase()}
                                        value={brakeMode === p.id}
                                        setValue={() => setBrakeMode(p.id)}
                                        inverse={true}
                                        flipped={true}
                                    />
                                </div>
                            })
                        }
                        <div style={{ marginTop: '20px' }}>
                            <Slider
                                label={'ADJUST SENSITIVITY'}
                                value={brakeSensitivity}
                                setValue={(value: number) => setBrakeSensitivity(value)}
                                max={maxBrakeSensitivity}
                                min={minBrakeSensitivity}
                                step={(maxBrakeSensitivity - minBrakeSensitivity) / (settings?.z_sensitivity?.steps ? (settings.z_sensitivity.steps - 1) : 4)}
                                bottomLeftLabel={'LOW'}
                                bottomRightLabel={`HIGH`}
                                inverse={true}
                            />
                        </div>
                    </div>}
                </Grid>
                <Grid size={{ xs: 6 }}>
                    {mode.eye_saver && type.eye_saver_compatible && mode.eye_saver_pattern && <div
                        className="mode-settings"
                        style={{ marginTop: '10px',  position: 'relative', paddingTop: '15px' }}
                    >
                        <InfoOutlinedIcon
                            sx={{ cursor: 'pointer', marginLeft: '10px' }}
                            className='top-right'
                            style={{ top: 0, right: 0}}
                            onClick={() => showPopup({
                                sub: true,
                                content:
                                    <div className="eye-info">
                                        <InfoOutlinedIcon
                                            className="top-left"
                                            sx={{ cursor: "pointer" }}
                                        />
                                        <p>
                                            LIMITS LIGHT BEAM TO HALF OF THE LIGHTS SURFACE
                                            TO PREVENT IT FROM SHINING INTO YOUR EYES WHEN MOUNTED
                                            HORIZONTALLY ON YOUR HANDLEBAR
                                        </p>
                                    </div>,
                                customClass: "button-small long-popup"
                            })}
                        />
                        <Toggle
                            value={eyeSaver}
                            setValue={(value) => setEyeSaver(value)}
                            leftLabel='ENABLE'
                            rightLabel='DISABLE'
                            title='EYE SAVER MODE'
                        />
                    </div>} 
                    <div className="mode-buttons">
                        <Button text="APPLY" className={pendingSave ? "radiate-effect" : ""} onClick={handleApply} />
                        <Button text="RESET" onClick={handleReset} />
                        <Button text="REMOVE" onClick={() => { handleRemove(); handleCancel(); }} />
                        <Button text="CANCEL" onClick={handleCancel} />
                    </div>          
                </Grid>
            </Grid>
        </div>
    );
}

export default FlashModeSettings;